import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'

import MenuDropdown from '~/components/common/inputs/MenuDropdown'
import ValidationErrorPopper from '~/components/common/validation/ValidationErrorPopper'
import FeatureToggle from '~/constants/featureToggle'
import {
  clearAppointmentValidationError,
  patchAppointment,
} from '~/store/actions/timetable'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getAppointmentId, getSoapBusinessId } from '~/store/reducers/soap'
import {
  getTimetableEvent,
  getTimetableValidationError,
} from '~/store/reducers/timetable'
import { addOriginalBusinessId } from '~/utils'

import SoapHeader from './SoapHeader'
import SoapHeaderTabs, { type SoapHeaderTabsProps } from './SoapHeaderTabs'

interface StyleProps {
  isAwoPmrRightRailEnabled: boolean
}

const useStyles = makeStyles(
  (theme) => ({
    headerContainer: {
      [theme.breakpoints.down('md')]: {
        top: 0,
      },
      top: (props: StyleProps) =>
        props.isAwoPmrRightRailEnabled
          ? theme.constants.soapMenuStickyTopHeight
          : theme.mixins.toolbar.minHeight,
      position: 'sticky',
      boxShadow: '0 2px 4px 0 rgba(0,0,0,0.1)',
      backgroundColor: theme.colors.contentBackground,
      zIndex: theme.utils.modifyZIndex(theme.zIndex.progress, 'below'),
    },
    soapDropdown: {
      '&:hover': {
        textDecoration: 'underline',
      },
      color: theme.colors.secondaryText,
      paddingBottom: 0,
      marginRight: `${theme.spacing(1)} !important`,
      marginLeft: `${theme.spacing(1)} !important`,
      whiteSpace: 'nowrap',
    },
  }),
  { name: 'SoapMenu' },
)

export interface SoapMenuProps extends SoapHeaderTabsProps {
  hideHeader?: boolean
}

export const SoapMenu = ({ tabs, hideHeader = false }: SoapMenuProps) => {
  const isAwoPmrRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AWO_PMR_RIGHT_RAIL),
  )
  const classes = useStyles({ isAwoPmrRightRailEnabled })

  const { t } = useTranslation('Common')

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { soapId: urlSoapId } = useParams()

  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )
  const appointmentId = useSelector(getAppointmentId)
  const appointment = useSelector(getTimetableEvent(appointmentId))
  const soapBusinessId = useSelector(getSoapBusinessId)
  const validationError = useSelector(getTimetableValidationError)

  const rootRef = useRef<HTMLDivElement>(null)

  const soaps =
    appointment?.soaps?.map((s, idx) => ({
      name: `${t('Common:SOAP')} ${idx + 1}`,
      value: s.id,
    })) || []
  const currentSoapIdx = R.findIndex(
    (s) => s.id === urlSoapId,
    appointment?.soaps ?? [],
  )

  const handleSoapChange = (currentSoapId: string) => {
    navigate(
      addOriginalBusinessId(
        `/soap/${currentSoapId}`,
        isPatientSharingEnabled ? soapBusinessId : null,
      ),
    )
  }

  return (
    <Box className={classes.headerContainer} ref={rootRef}>
      {!hideHeader && <SoapHeader />}
      <Box display="flex">
        {soaps.length > 1 && (
          <MenuDropdown
            linkButton
            className={classes.soapDropdown}
            items={soaps}
            title={`${t('Common:SOAP')} ${currentSoapIdx + 1}`}
            onSelected={handleSoapChange}
          />
        )}
        <SoapHeaderTabs tabs={tabs} />
      </Box>
      <ValidationErrorPopper
        anchorEl={rootRef.current}
        error={validationError}
        open={Boolean(validationError)}
        placement="bottom"
        proceedButtonActionName={t('Common:EDIT_ACTION')}
        onBack={() => {
          dispatch(clearAppointmentValidationError())
        }}
        onProceed={() => {
          dispatch(
            patchAppointment({
              id: appointment?.id!,
              bypassValidation: true,
            }),
          )
        }}
      />
    </Box>
  )
}
